import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, LogOut, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabaseHelpers } from '../lib/supabase';

// Blocking screen shown to any signed-in user whose email is not yet confirmed
// (no grace period — verification is required from the start). They can hit the
// resend button and verify their address — once confirmed, onConfirmed is called
// with the refreshed user object and the parent unlocks the app. Until then, this
// screen takes over the viewport so the rest of the UI is unreachable.

export default function EmailConfirmationLock({ user, onSignOut, onConfirmed }) {
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resending || resendCooldown > 0) return;
    setResending(true);
    try {
      const { error } = await supabaseHelpers.resendSignupConfirmation(user.email);
      if (error) throw error;
      toast.success('Confirmation email sent — check your inbox.');
      setResendCooldown(60);
    } catch (err) {
      toast.error(err.message || 'Failed to resend confirmation email');
    } finally {
      setResending(false);
    }
  };

  // Poll-once check after the user claims to have clicked the link.
  // We refresh the Supabase session and look at email_confirmed_at; if
  // it's now populated the parent unlocks and lets the app load.
  const handleCheck = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const { user: refreshed } = await supabaseHelpers.getCurrentUser();
      if (refreshed?.email_confirmed_at) {
        toast.success('Email confirmed. Welcome back.');
        onConfirmed?.(refreshed);
      } else {
        toast.info("Still not confirmed — open the link in the email and try again.");
      }
    } catch (err) {
      toast.error('Could not check confirmation status. Try again in a moment.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
        <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-violet-600 dark:text-violet-400" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-3">
          Confirm your email to continue
        </h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Please confirm your email to start using Wizardly. Click the
          confirmation link we sent to:
        </p>
        <p className="font-mono text-sm font-medium text-foreground bg-muted px-3 py-2 rounded-lg mb-6 break-all">
          {user?.email}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleCheck}
            disabled={checking}
            className="w-full py-3 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            I've confirmed it
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resendCooldown > 0}
            className="w-full py-3 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend confirmation email'}
          </button>
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut className="w-3 h-3" />
          Sign out
        </button>
      </div>
    </div>
  );
}
